import { supabase } from './supabase';
import { checkVideoUploadSize } from '../utils/mediaUploadLimits';

const TEMP_BUCKET = 'video-uploads';
const FUNCTION_NAME = 'bunny-video';

export type BunnyVideoMetadata = {
  title: string;
  description?: string;
  tags?: string[];
  category?: string;
};

export type BunnyVideoDetails = {
  videoId: string;
  videoUrl: string;
  thumbnailUrl?: string;
  duration?: number;
  size?: number;
};

export type BunnyVideoUploadResult = {
  success: boolean;
  data?: BunnyVideoDetails;
  error?: string;
};

async function callBunnyFunction(
  action: 'upload' | 'details' | 'delete',
  payload: Record<string, unknown>
): Promise<BunnyVideoUploadResult> {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: { action, ...payload },
  });

  if (error) {
    return { success: false, error: error.message };
  }

  if (!data?.success) {
    return { success: false, error: data?.error || 'Bunny request failed' };
  }

  return { success: true, data: data.data as BunnyVideoDetails };
}

async function uploadVideoToTempStorage(
  file: File,
  userId: string
): Promise<{ success: boolean; path?: string; size?: number; error?: string }> {
  const extensionFromName = file.name.includes('.') ? (file.name.split('.').pop() ?? 'mp4') : 'mp4';
  const safeExtension = extensionFromName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'mp4';
  const storagePath = `bunny-uploads/${userId}/${Date.now()}.${safeExtension}`;

  const { error: uploadError } = await supabase.storage.from(TEMP_BUCKET).upload(storagePath, file, {
    contentType: file.type || 'video/mp4',
    upsert: true,
  });

  if (uploadError) {
    return { success: false, error: uploadError.message };
  }

  return { success: true, path: storagePath, size: file.size };
}

async function storeVideoMetadata(
  videoId: string,
  metadata: BunnyVideoMetadata,
  userId: string,
  videoDetails: BunnyVideoDetails
): Promise<void> {
  const { error } = await supabase.from('user_videos').insert({
    bunny_video_id: videoId,
    title: metadata.title,
    description: metadata.description,
    tags: metadata.tags,
    category: metadata.category,
    uploaded_by: userId,
    video_url: videoDetails.videoUrl,
    thumbnail_url: videoDetails.thumbnailUrl,
    duration: videoDetails.duration,
    file_size: videoDetails.size,
    status: 'processing',
    created_at: new Date().toISOString(),
  });

  if (error) {
    console.warn('Failed to store Bunny video metadata:', error.message);
  }
}

/**
 * Upload a browser File to Bunny Stream using the same path as mobile:
 * temp Supabase bucket (`video-uploads`) -> `bunny-video` edge function.
 */
export async function uploadVideoToBunny(
  file: File,
  metadata: BunnyVideoMetadata,
  userId: string
): Promise<BunnyVideoUploadResult> {
  if (!file.type.startsWith('video/')) {
    return { success: false, error: 'Please choose a valid video file.' };
  }

  const sizeCheck = checkVideoUploadSize(file.size);
  if (!sizeCheck.ok) {
    return { success: false, error: sizeCheck.reason };
  }

  const tempUpload = await uploadVideoToTempStorage(file, userId);
  if (!tempUpload.success || !tempUpload.path) {
    return { success: false, error: tempUpload.error || 'Failed to upload video for processing.' };
  }

  const uploadResponse = await callBunnyFunction('upload', {
    storageBucket: TEMP_BUCKET,
    storagePath: tempUpload.path,
    metadata,
  });

  if (!uploadResponse.success || !uploadResponse.data) {
    return { success: false, error: uploadResponse.error || 'Failed to upload video to Bunny.' };
  }

  const videoData: BunnyVideoDetails = {
    videoId: uploadResponse.data.videoId,
    videoUrl: uploadResponse.data.videoUrl,
    thumbnailUrl: uploadResponse.data.thumbnailUrl,
    duration: uploadResponse.data.duration,
    size: tempUpload.size ?? uploadResponse.data.size,
  };

  await storeVideoMetadata(videoData.videoId, metadata, userId, videoData);

  return { success: true, data: videoData };
}
